import Vue from 'vue';
import Router from 'vue-router';
import Account from "@/components/page-components/Account/Account.vue";
import Login from "@/components/page-components/Login.vue";
import ForgotPasswordPage from "@/components/page-components/ForgotPasswordPage.vue";
import Register from "@/components/page-components/Registration/Register.vue";
import ErrorSSOLanding from "@/components/page-components/ErrorSSOLanding.vue";
import PasswordReset from "@/components/page-components/Account/PasswordReset.vue";
import EditCustomer from "@/components/page-components/Account/Customer/EditCustomer.vue";
import TopUp from "@/components/page-components/TopUp.vue";
import OrderHistory from "@/components/page-components/Order/OrderHistory.vue";
import TransactionHistory from "@/components/page-components/Transaction/TransactionHistory.vue";
import StudentAccounts from "@/components/page-components/StudentAccounts.vue";
import AccountConfirm from "@/components/page-components/AccountConfirm.vue";
import PaymentEnd from "@/components/page-components/PaymentEnd.vue";
import LoginSSO from "@/components/page-components/LoginSSO.vue";
import MealPlanner from "@/components/page-components/MealPlanner/MealPlanner.vue";
import CustomerCard from "@/components/page-components/CustomerCard.vue";
import Dashboard from "@/components/page-components/Dashboard/Dashboard.vue";
import Template from "@/components/page-components/templates/Template.vue";
import EditAddress from '@/components/page-components/Account/Address/EditAddress.vue'
import {useAuthStore} from "@/stores/auth";
import pinia from "@/stores";
import ErrorLanding from "@/components/page-components/ErrorLanding.vue";
import Landing from "@/components/page-components/Landing.vue";
import AutoTopUp from "../components/page-components/AutoTopUp.vue";
import {useAppStore} from "@/stores/app";
import {Login as LoginConstants, Mode, Order} from "@/constants/constants";
import TopUpPayment from "@/components/page-components/TopUpPayment.vue";
import I18n, {loadLocaleMessages} from "@/i18n";
import {useLocaleStore} from "@/stores/locale";
import MealPlanOverview from "@/components/page-components/MealPlanner/MealPlanOverview.vue";
import OrderMenuPlanner from "@/components/page-components/Order/OrderMenuPlanner.vue";
import OrderMenuCheckout from "@/components/page-components/Order/OrderMenuCheckout.vue";
import MealPlanCheckout from "@/components/page-components/MealPlanner/MealPlanCheckout.vue";
import OrderMenuConfirmation from "@/components/page-components/Order/OrderMenuConfirmation.vue";
import OrderMenuParameters from "@/components/page-components/Order/OrderMenuParameters.vue";
import MealPlanParameters from "@/components/page-components/MealPlanner/MealPlanParameters.vue";
import RegisterChild from "@/components/page-components/Registration/RegisterChild.vue";
import AddressBook from "@/components/page-components/Account/Address/AddressBook.vue";
import RegisterGuest from "@/components/page-components/Registration/RegisterGuest.vue";
import CreateAddress from "@/components/page-components/Account/Address/CreateAddress.vue";
import {apiUtils} from "@/utils/apiUtils";
import TableOrder from "../components/page-components/Table/TableOrder.vue";
import TakeawayOrder from "../components/page-components/Takeaway/TakeawayOrder.vue";
import {getAuthenticatedHomeRouteName, isGuestRestrictedRouteName} from "@/utils/authRouteUtils";

Vue.use(Router);

let config = window.config;

// Support legacy hash-mode links like `/#/qrlanding?...` while running in history mode.
// In history mode Vue Router ignores the hash for route matching, so we manually migrate it once.
let legacyHashRedirectHandled = false;
function getLegacyHashRedirectTarget() {
    const hash = (window.location && window.location.hash) ? window.location.hash : '';
    if (!hash || !hash.startsWith('#/')) return null;

    const raw = hash.slice(1); // remove leading '#'
    const qIndex = raw.indexOf('?');
    const hashPath = (qIndex >= 0 ? raw.slice(0, qIndex) : raw);
    const queryString = (qIndex >= 0 ? raw.slice(qIndex + 1) : '');

    // Only migrate the legacy hash route we care about.
    if (!/^\/qrlanding$/i.test(hashPath)) return null;

    const query = {};
    if (queryString) {
        const params = new URLSearchParams(queryString);
        for (const [k, v] of params.entries()) query[k] = v;
    }

    // Normalize to the actual route path used by this app.
    // Use lowercase as canonical path; keep `/qrLanding` supported via route alias.
    return { path: '/qrlanding', query };
}

function getLandingOrderTypeId(route) {
    const rawOrderTypeId = route?.query?.orderTypeId ?? route?.query?.ot;

    if (rawOrderTypeId === undefined || rawOrderTypeId === null || rawOrderTypeId === '') {
        return undefined;
    }

    if (String(rawOrderTypeId).toLowerCase() === Order.type.TAKEAWAY) {
        return Order.numericType.TAKEAWAY;
    }

    const parsedOrderTypeId = Number(rawOrderTypeId);
    return Number.isFinite(parsedOrderTypeId) ? parsedOrderTypeId : undefined;
}

const router = new Router({
    mode: 'history',
    base: '/dist/',
    routes: [
        {
            path: '/',
            redirect: '/login',
        },
        {
            path: '/dashboard',
            name: 'Dashboard',
            component: Dashboard,
            meta: { title: 'system.title.dashboard', requiresLogin: true }
        },
        {
            path: '/template',
            name: 'Template',
            component: Template,
            meta: { enabled: process.env.NODE_ENV !== 'production', title: 'Template' }
        },
        {
            path: '/login',
            name: 'Login',
            component: Login,
            meta: { title: 'login.title', ariaTitle: 'ariaLabel.login' }
        },
        {
            path: '/loginSSO',
            name: 'LoginSSO',
            component: LoginSSO,
            props: route => ({
                code: route.query.code,
                state: route.query.state,
                jwt: route.query.id_token,
                tableCode: route.query.t,
                shopCode: route.query.s,
                orderImmediate: route.query.i,
                isMealPlan: route.query.isMealPlan,
                isSchool: route.query.isSchool,
                validationType: route.query.validationType,
                oneTimeToken: route.query.ott
            }),
        },
        {
            path: '/register',
            name: 'Register',
            component: Register,
            meta: { enabled: config.REGISTRATION.enabled, title: 'register.title.register', showBackButton: '/login' }
        },
        {
            path: '/registerParent',
            name: 'RegisterParent',
            component: Register,
            props: { mode: 'parent' },
            meta: { enabled: config.STUDENT_ACCOUNTS.enabled, title: 'register.title.registerParent', showBackButton: '/login' }
        },
        {
            path: '/registerGuest',
            name: 'RegisterGuest',
            component: RegisterGuest,
            meta: { enabled: config.GUEST_CHECKOUT.enabled, title: 'register.title.registerGuest', showBackButton: '/login' }
        },
        {
            path: '/forgotPassword',
            name: 'ForgotPassword',
            component: ForgotPasswordPage,
            meta: { title: 'forgotPassword.title', showBackButton: '/login' }
        },
        {
            path: '/mealPlan/parameters',
            name: 'MealPlanParameters',
            component: MealPlanParameters,
            meta: { enabled: config.MEAL_PLAN.enabled, title: 'mealPlanParameters.title' },
        },
        {
            path: '/mealPlan/overview',
            name: 'MealPlanOverview',
            component: MealPlanOverview,
            meta: { enabled: config.MEAL_PLAN.enabled, title: 'system.title.downloads', showBackButton: true },
        },
        {
            path: '/order/parameters/:shopId?',
            name: 'OrderMenuParameters',
            component: OrderMenuParameters,
            meta: { enabled: config.ORDER.enabled, title: 'orderParameters.title' },
            props: true
        },
        {
            path: '/order/menu',
            name: 'OrderMenuPlanner',
            component: OrderMenuPlanner,
            meta: { enabled: config.ORDER.enabled, title: 'orderMenu.title', requiresLogin: window.config.FORCE_LOGIN_BEFORE_USE, showBackButton: '/order/shops' }
        },
        // Pages with required login
        {
            path: '/account',
            name: 'Account',
            component: Account,
            meta: { enabled: config.ACCOUNT.enabled, title: 'account.title', requiresLogin: true }
        },
        {
            path: '/account/passwordReset',
            name: 'PasswordReset',
            component: PasswordReset,
            meta: { title: 'passwordReset.title', showBackButton: '/account', requiresLogin: false }
        },
        {
            path: '/account/addressBook',
            name: 'AddressBook',
            component: AddressBook,
            meta: { enabled: config.ADDRESS.enabled, title: 'addressBook.title.addressBook', showBackButton: '/account', requiresLogin: true },
            props: true
        },
        {
            path: '/account/editAddress/:id',
            name: 'EditAddress',
            component: EditAddress,
            meta: { enabled: config.ADDRESS.enabled, title: 'addressBook.title.editAddress', showBackButton: '/account/addressBook', requiresLogin: true },
            props: true,
        },
        {
            path: '/account/newAddress',
            name: 'CreateAddress',
            component: CreateAddress,
            meta: { enabled: config.ADDRESS.enabled, title: 'addressBook.title.newAddress', showBackButton: '/account/addressBook', requiresLogin: true },
            props: true,
        },
        {
            path: '/account/editCustomer',
            name: 'EditCustomer',
            component: EditCustomer,
            meta: { enabled: config.CUSTOMER_DATA.editable, title: 'editCustomer.title', showBackButton: '/account', requiresLogin: true }
        },
        {
            path: '/customerCard',
            name: 'CustomerCard',
            component: CustomerCard,
            meta: { enabled: config.CUSTOMER_CARD.enabled, title: 'customerCard.title', requiresLogin: true },
        },
        // TODO store customer infos in pinia store and remove this route
        {
            path: '/customerCard/topup/:customerId?',
            name: 'TopUp',
            component: TopUp,
            meta: { enabled: config.CUSTOMER_CARD.topUp.enabled, title: 'topUp.title', showBackButton: '/customerCard', requiresLogin: true },
            props: true
        },
        // TODO store customer infos in pinia store and remove this route
        {
            path: '/customerCard/topup/confirm/payment/:customerId?',
            name: 'TopUpPayment',
            component: TopUpPayment,
            meta: { enabled: config.CUSTOMER_CARD.topUp.enabled, title: 'topUpPayment.title', showBackButton: '/customerCard', requiresLogin: true },
            props: true
        },
        {
            path: '/customerCard/autoTopup/:customerId?',
            name: 'AutoTopUp',
            component: AutoTopUp,
            meta: { enabled: config.CUSTOMER_CARD.autoTopUp.enabled, title: 'autoTopUp.title', showBackButton: '/customerCard', requiresLogin: true },
            props: true
        },
        {
            path: '/orderHistory',
            name: 'OrderHistory',
            component: OrderHistory,
            meta: { enabled: config.ORDER_HISTORY.enabled, title: 'orderHistory.title', requiresLogin: true }
        },
        {
            path: '/transactionHistory/:customerId?',
            name: 'TransactionHistory',
            component: TransactionHistory,
            meta: { enabled: config.TRANSACTION_HISTORY.enabled, title: 'transactionHistory.title', requiresLogin: true },
            props: true
        },
        {
            path: '/mealPlan/meals',
            name: 'MealPlanner',
            component: MealPlanner,
            meta: { enabled: config.MEAL_PLAN.enabled, title: 'mealPlan.title', requiresLogin: true, showBackButton: '/mealPlan/shops', showDownloadButton: config.MEAL_PLAN.view.enabled },
            props: route => ({ isMealPlan: route.query.isMealPlan })
        },
        {
            path: '/studentAccounts',
            name: 'StudentAccounts',
            component: StudentAccounts,
            meta: { enabled: config.STUDENT_ACCOUNTS.enabled, title: 'studentAccounts.title', requiresLogin: true },
        },
        {
            path: '/registerChild',
            name: 'RegisterChild',
            component: RegisterChild,
            meta: { enabled: config.STUDENT_ACCOUNTS.enabled, title: 'register.title.registerChild', showBackButton: '/login', requiresLogin: true }
        },
        {
            path: '/order/checkout',
            name: 'OrderMenuCheckout',
            component: OrderMenuCheckout,
            meta: { title: 'checkout.title', showBackButton: '/order/menu', requiresLogin: true }
        },
        {
            path: '/mealPlan/checkout',
            name: 'MealPlanCheckout',
            component: MealPlanCheckout,
            meta: { title: 'checkout.title', showBackButton: '/mealPlan/mealPlan', requiresLogin: true }
        },

        // Pages to redirect and replace browser history
        {
            path: '/confirm',
            name: 'Confirm',
            component: AccountConfirm,
            props: route => ({ k: route.query.key, m: route.query.m, i: route.query.i})
        },
        {
            path: '/landing',
            name: 'Landing',
            component: Landing,
            props: route => ({
                tableCode: route.query.t,
                shopCode: route.query.s,
                orderImmediate: route.query.i,
                orderTypeId: getLandingOrderTypeId(route),
                isMealPlan: route.query.isMealPlan,
                cardNumber: route.query.cn,
                payAtTable: route.query.payAtTable === 'true',
                decideOnTable: route.query.d === 'true'
            }),
        },
        {
            path: '/qrlanding',
            name: 'QrLanding',
            component: Landing, // Use the same component as Landing
            alias: ['/qrLanding'],
            props: route => ({
                tableCode: route.query.t,
                shopCode: route.query.s,
                orderImmediate: route.query.i,
                orderTypeId: getLandingOrderTypeId(route),
                isMealPlan: route.query.isMealPlan,
                cardNumber: route.query.cn,
                payAtTable: route.query.payAtTable === 'true',
                decideOnTable: route.query.d === 'true'
            }),
        },
        {
            path: '/errorSSO',
            name: 'ErrorSSO',
            component: ErrorSSOLanding
        },
        {
            path: '/errorLanding',
            name: 'ErrorLanding',
            component: ErrorLanding,
            props: route => ({ sessionTimeout: route.query.sessionTimeout === 'true' || false })
        },
        {
            path: '/order/confirmation',
            name: 'OrderMenuConfirmation',
            component: OrderMenuConfirmation,
            meta: { title: 'orderConfirmation.title', requiresLogin: true },
        },
        {
            path: '/table/order',
            name: 'TableOrder',
            component: TableOrder,
            meta: { title: 'tableOrder.title', requiresLogin: true },
            props: true
        },
        {
            path: '/takeaway/order',
            name: 'TakeawayOrder',
            component: TakeawayOrder,
            meta: { enabled: config.TAKEAWAY_ORDER.enabled, title: 'takeawayOrder.title', requiresLogin: true },
            props: true
        },
        {
            path: '/paymentEnd',
            name: 'PaymentEnd',
            component: PaymentEnd,
            meta: { requiresLogin: true },
            props: route => ({
                payment: route.query.p,
                result: route.query.r,
                uuid: route.query.uuid,
                sessionId: route.query.sessionId,
                sessionResult: route.query.sessionResult,
                hostedCheckoutId: route.query.hostedCheckoutId
            }),
        },
    ],
});

router.beforeEach(async (to, from, next) => {
    console.debug(`Navigating from ${from.fullPath} to ${to.fullPath}`);

    // One-time migration for legacy hash-mode URLs like `/#/qrlanding?...`.
    if (!legacyHashRedirectHandled) {
        const target = getLegacyHashRedirectTarget();
        if (target && to.path !== target.path) {
            legacyHashRedirectHandled = true;
            return next({ ...target, replace: true });
        }
        legacyHashRedirectHandled = true;
    }

    const authStore = useAuthStore(pinia);
    const appStore = useAppStore(pinia);
    const localeStore = useLocaleStore(pinia);

    if (!I18n.availableLocales.includes(localeStore.getLocale)) {
        await loadLocaleMessages(I18n,localeStore.getLocale);
    }

    // Skip guard if navigating to the same route
    if (to.fullPath === from.fullPath) {
        return next(false);
    }

    if(!authStore.loginChecked && config.LOGIN.default.rememberMe) {
        authStore.setLoginChecked(true);

        const timeoutChecker = new AbortController();
        setTimeout(async () => {timeoutChecker.abort('timeout');console.log("Aboted")}, 5000);
        try {

            const result = await apiUtils.doGet('/TCPCheckLoginJ.action', {
                json: "true"
            },{
                signal: timeoutChecker.signal
            });

            if (result.loggedIn) {
                // Preserve the currently authenticated session type (for example guest checkout)
                // and only infer a registered user when no local auth state exists yet.
                if (!authStore.isLoggedIn) {
                    authStore.setAuthenticated(true, LoginConstants.Mode.STANDARD, LoginConstants.UserType.REGISTERED_USER);
                }
            } else {
                authStore.$reset();
            }
        } catch (e) {/* ignore */}

    }
    // Authentication check and save route name before redirecting to login
    if (!authStore.isLoggedIn && to.meta.requiresLogin) {
        console.log("User not authenticated, saving redirect route:", to.name);
        appStore.setRedirectRoute(to.name, to.params, to.query);
        return appStore.isMode(Mode.IMMEDIATE) ? next({name: 'RegisterGuest'}) : next({name: 'Login'});
    }

    if (authStore.isLoggedIn && authStore.isGuest && isGuestRestrictedRouteName(to.name)) {
        console.log("Guest user attempted to access a registered-only route:", to.name);
        return next({ name: getAuthenticatedHomeRouteName(config, authStore), replace: true });
    }

    if ((appStore.isMode(Mode.TABLE) || appStore.isMode(Mode.TAKEAWAY)) && to.name === 'OrderMenuParameters') {
        // Allow navigation to OrderMenuParameters in TABLE mode only if explicitly enabled (e.g., for TABLE decision flow)
        if (!appStore.allowTableModeOrderParametersNavigation) {
            const orderEntryRouteName = appStore.orderEntryRouteName || appStore.tableOrderEntryRouteName || 'OrderMenuPlanner';
            console.log("Active special order session prevents generic order parameter navigation, redirecting to:", orderEntryRouteName);
            return next({ name: orderEntryRouteName, replace: true });
        }
        // After using the flag once, disable it to prevent subsequent access
        appStore.disallowTableModeParametersNavigation();
    }

    // Prevent logged-in users from accessing Login
    if (authStore.isLoggedIn && (to.name === 'Login' || to.name === 'LoginSSO')) {
        console.log("User already logged in, redirecting to default page or dashboard");
        return next({ name: getAuthenticatedHomeRouteName(config, authStore), replace: true });
    }

    // Feature enabled check
    const isEnabled = typeof to.meta.enabled === 'boolean' ? to.meta.enabled : true;
    if (!isEnabled) {
        console.log("Feature not enabled, redirecting to default page or dashboard");
        if (authStore.isLoggedIn) {
            return next({ name: getAuthenticatedHomeRouteName(config, authStore), replace: true });
        }

        return next(config.DEFAULT_START_PAGE.enabled
            ? { name: config.DEFAULT_START_PAGE.name }
            : { path: '/dashboard' });
    }

    // Update `showBackButton`
    if (['/order/menu', '/mealPlan/meals', '/order/shop'].includes(to.path)) {
        if (appStore.isMode(Mode.TABLE) || appStore.isMode(Mode.TAKEAWAY) || appStore.isMode(Mode.IMMEDIATE)) {
            to.meta.showBackButton = undefined; // unset Show Back Button
        }
    }

    next();
});

export default router;
